import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { identitiesFilteredOnAccess } from '../../../../../util/identity';
import { getAsset } from '../../../../../util/assets';
import { isAlbum } from '../../../../../util/album';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../../../constants/album-folder';
import { MeQuery } from '../../../../../graphql/queries/account';
import { getAlbumMembershipGroupQuery } from '../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroup';
import { albumFolderJoin as albumFolderJoinMutation } from '../../../../../graphql/mutations/album-folder';
import { albumsJoinByCode as albumsJoinByCodeMutation } from '../../../../../graphql/mutations/albums';
import PhoneInput from '../../../../../components/global/PhoneInput';
import Button from '../../../../../components/global/Button';
import { getErrorString } from '../../../../../util/normalize-errors';
import Alert from '../../../../../components/global/Alert';
import PinField from '../../../../../components/global/PinField';
import { ALBUM_IDENTITY_ACCESS } from '../../../../../constants/identity';
import usePhoneAuth from '../../../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../../../services/recaptcha/useGoogleRecaptcha';
import CustomControl from '../../../../../components/global/CustomControl';

const propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingUser: PropTypes.bool,
  loadingAlbumMembershipGroup: PropTypes.bool,
  albumFolderJoin: PropTypes.func.isRequired,
  albumsJoinByCode: PropTypes.func.isRequired,
  authenticated: PropTypes.bool,
};

const STEPS = {
  PHONE_AUTH: 'PHONE_AUTH',
  PIN_VERIFY: 'PIN_VERIFY',
  AUTHENTICATED: 'AUTHENTICATED',
};

const WebGalleryAutoJoinNullState = ({
  entity,
  user,
  albumMembershipGroup,
  loadingUser,
  loadingAlbumMembershipGroup,
  albumFolderJoin,
  albumsJoinByCode,
  authenticated,
}) => {
  const [step, setStep] = useState(
    authenticated ? STEPS.AUTHENTICATED : STEPS.PHONE_AUTH,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    loading: phoneAuthLoading,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
  } = usePhoneAuth();

  useEffect(() => {
    if (pinSent && step !== STEPS.PIN_VERIFY) {
      setStep(STEPS.PIN_VERIFY);
    }
  }, [pinSent]);

  const onJoin = () => {
    const [primaryIdentity] = identitiesFilteredOnAccess(
      user.linked_identities?.records ?? [],
      ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY,
    );

    if (primaryIdentity) {
      const albumFolder = isAlbum(entity) ? entity.root_album_folder : entity;

      if (albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL) {
        albumFolderJoin({
          album_folder_id: albumFolder?.id,
          identity_ids: [primaryIdentity.id],
        });
      }

      // This will not really support galleries with sub-folders.
      // The ideal scenario is for Join type to always be join-all.
      // This is mainly a safeguard for situations which we can do something about.
      if (albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.INDIVIDUAL) {
        // Attempt to get 1 child.

        const album = isAlbum(entity)
          ? entity
          : albumFolder.children?.albums?.records?.[0] ?? null;

        if (!album) {
          return;
        }

        albumsJoinByCode({
          account_id: user?.id,
          identities: [primaryIdentity.id],
          join_codes: [album.join_code],
        });
      }
    }
  };

  const onRequestPIN = async ({
    phone_number: phoneNumber,
    force_allow_action: forceAllowAction,
  }) => {
    setLoading(true);
    setError('');

    try {
      const token = onTokenRequest({ action: 'login ' });
      await onPhoneAuthSubmit(phoneNumber, true, token, forceAllowAction);
      if (step !== STEPS.PIN_VERIFY) {
        setStep(STEPS.PIN_VERIFY);
      }
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onPhoneLogin = async ({ phone_number: phoneNumber, code }) => {
    setLoading(true);
    setError('');

    try {
      await onPhoneAuthVerify(phoneNumber, code);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = values => {
    if (step === STEPS.PHONE_AUTH) {
      onRequestPIN(values);
    }

    if (step === STEPS.PIN_VERIFY) {
      onPhoneLogin(values);
    }
  };

  useEffect(() => {
    if (
      user &&
      !albumMembershipGroup &&
      !loadingAlbumMembershipGroup &&
      !loadingUser &&
      authenticated &&
      step === STEPS.AUTHENTICATED
    ) {
      onJoin();
    }
  }, [user, albumMembershipGroup, step]);

  useEffect(() => {
    if (authenticated) {
      setStep(STEPS.AUTHENTICATED);
    }
  }, [authenticated]);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center px-2">
      <img
        src={getAsset('/waldo-icon.svg')}
        className="grayscale-faded mb-3"
        width="40"
        alt="Waldo Icon"
      />
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          phone_number: '+1',
          code: '',
          force_allow_action: false,
        }}
        validateOnBlur={false}
        validateOnChange={false}
        render={({ values, handleSubmit, handleChange }) => {
          const isValidPIN = values.code?.length === 4;

          return (
            <>
              {error && (
                <Alert type="danger" className="mt-2">
                  {error}
                </Alert>
              )}
              {step === STEPS.PHONE_AUTH && (
                <>
                  <h3>
                    No photos have been uploaded to this event yet. <br />
                    Sign up and be the first to get notified when photos are
                    added!
                  </h3>
                  <p>
                    Add your phone number and we&apos;ll text you when it&apos;s
                    time.
                  </p>
                  <form
                    onSubmit={handleSubmit}
                    className="w-100 px-0 px-md-6 d-flex flex-column align-items-center"
                  >
                    <PhoneInput
                      id="phone_number"
                      label=""
                      value={values.phone_number || '+1'}
                      onChange={phone =>
                        handleChange({
                          target: {
                            name: 'phone_number',
                            value: phone,
                          },
                        })
                      }
                      className="w-50 mb-0"
                      autoFocus
                    />
                    {requireHumanInput && (
                      <div className="w-50 text-left mt-2">
                        <CustomControl
                          type="checkbox"
                          label="I'm not a robot"
                          checked={values.force_allow_action}
                          onChange={() =>
                            handleChange({
                              target: {
                                name: 'force_allow_action',
                                value: !values.force_allow_action,
                              },
                            })
                          }
                        />
                      </div>
                    )}
                    <Button
                      type="submit"
                      className="mt-2 mb-4"
                      disabled={
                        loading ||
                        phoneAuthLoading ||
                        values.phone_number === '+1' ||
                        blockLogin ||
                        (requireHumanInput && !values.force_allow_action)
                      }
                    >
                      {loading ? 'Processing...' : 'Continue'}
                    </Button>
                  </form>
                </>
              )}
              {step === STEPS.PIN_VERIFY && (
                <>
                  <h3>
                    Please enter the code sent to{' '}
                    <span className="text-primary">{values.phone_number}</span>
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    className="w-100 px-0 px-md-6 d-flex flex-column align-items-center"
                  >
                    <div className="mb-2">
                      <PinField
                        onComplete={value => {
                          handleChange({
                            target: {
                              name: 'code',
                              value,
                            },
                          });
                        }}
                        onChange={value => {
                          handleChange({
                            target: {
                              name: 'code',
                              value,
                            },
                          });
                        }}
                      />
                    </div>

                    <Button type="submit" disabled={loading || !isValidPIN}>
                      {loading ? 'Processing...' : 'Submit'}
                    </Button>

                    <div className="text-xs mt-2">
                      <p className="text-xs mb-0">
                        Didn&apos;t receive a code?
                      </p>
                      <Button
                        type="button"
                        btnStyle="link"
                        disabled={loading}
                        loading={loading}
                        btnSize="xs"
                        className="text-extra-bold"
                        onClick={() => onRequestPIN(values)}
                      >
                        Re&ndash;send code
                      </Button>
                    </div>
                  </form>
                </>
              )}
              {step === STEPS.AUTHENTICATED && (
                <>
                  <h3>
                    Sign up successful &ndash; No photos have been uploaded to
                    this event yet.
                  </h3>
                  <p>
                    We&apos;ll send you a text when photos are added to this
                    event!
                  </p>
                </>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

WebGalleryAutoJoinNullState.propTypes = propTypes;

const withUser = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const withAlbumMembershipGroup = graphql(getAlbumMembershipGroupQuery, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
    fetchPolicy: 'cache-first',
  }),
  props: ({ data: { albumMembershipGroup, loading } }) => ({
    albumMembershipGroup,
    loading,
  }),
  skip: ({ match, authenticated }) => !match.params.folderId || !authenticated,
});

const withAlbumFolderJoin = graphql(albumFolderJoinMutation, {
  props: ({ mutate, ownProps: { match } }) => ({
    albumFolderJoin: ({ album_folder_id, identity_ids }) =>
      mutate({
        variables: {
          input: {
            album_folder_id,
            identity_ids,
            sync_album_joins: true,
          },
        },
        refetchQueries: [
          {
            query: getAlbumMembershipGroupQuery,
            variables: {
              id: match.params.folderId,
            },
          },
        ],
      }),
  }),
});

const withAlbumsJoinByCode = graphql(albumsJoinByCodeMutation, {
  props: ({ mutate, ownProps: { match } }) => ({
    albumsJoinByCode: ({ account_id, join_codes = [], identities }) =>
      mutate({
        variables: {
          input: {
            account_id,
            join_codes,
            identities,
          },
        },
        refetchQueries: [
          {
            query: getAlbumMembershipGroupQuery,
            variables: {
              id: match.params.folderId,
            },
          },
        ],
      }),
  }),
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export { WebGalleryAutoJoinNullState as WebGalleryAutoJoinNullStateComponent };

export default compose(
  withRouter,
  connect(mapStateToProps),
  withUser,
  withAlbumMembershipGroup,
  withAlbumFolderJoin,
  withAlbumsJoinByCode,
)(WebGalleryAutoJoinNullState);
